<template>
  <f7-page :page-content="true">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title v-if="id && !readonly" sliding>{{$t('properties.editar_property')}}</f7-nav-title>
      <f7-nav-title v-else-if="id" sliding>{{$t('properties.property')}}</f7-nav-title>
      <f7-nav-title v-else sliding>{{$t('properties.nueva_property')}}</f7-nav-title>
      <f7-nav-right>
        <f7-button
          v-if="!readonly"
          fill
          icon-f7="floppy_disk"
          :text="$t('common.guardar')"
          class="display-flex"
          @click="$refs.rform.saveItem()"
        ></f7-button>
        <template slot="right" v-else>
          <f7-button color="blue" @click="activarEdicion">{{$t('common.editar')}}</f7-button>
          <f7-button color="red" @click="deleteItem">{{$t('common.eliminar')}}</f7-button>
        </template>
      </f7-nav-right>
    </f7-navbar>

    <r-form
      ref="rform"
      v-slot="{rform}"
      :itemId="id"
      @save="onSave"
      @loadItem="onLoadItem"
      :readonly="readonly"
      :dbAdapter="Property"
      :defaultData="defaultData"
    >
      <f7-card class="padding-vertical-half padding-right">
        <f7-card-content>
          <f7-row no-gap class="list list-form no-hairlines">
            <f7-col width="50" medium="50" small="100">
              <r-input
                outline
                class="col-100"
                :class="{'codigo-readonly': !!rform.formData.id}"
                floating-label
                :label="$t('properties.nombre')"
                :form="rform"
                type="text"
                name="nombre"
                :readonly="rform && rform.formData && !!rform.formData.id"
                :overrideReadonly="rform && rform.formData && !!rform.formData.id"
              ></r-input>
            </f7-col>

            <f7-col width="50" medium="50" small="100">
              <r-input
                outline
                class="col-100"
                floating-label
                :label="$t('properties.tipoCifrado')"
                :form="rform"
                type="select"
                name="tipoCifrado"
                :disabled="readonly"
              >
                <option value="NONE">{{$t('properties.cifrado_none')}}</option>
                <option value="AES_GCM_256">{{$t('properties.cifrado_aes')}}</option>
              </r-input>
            </f7-col>

            <f7-col width="100" medium="100" small="100">
              <div class="valor-wrapper">
                <r-input
                  outline
                  class="col-100"
                  floating-label
                  :label="$t('properties.valor')"
                  :form="rform"
                  :type="tipoInputValor(rform)"
                  name="valor"
                ></r-input>
                <!-- El ojo solo tiene sentido en las cifradas: en las NONE el valor ya se ve. -->
                <i
                  v-if="esCifrada(rform)"
                  class="f7-icons input-eye"
                  @click="mostrarValor = !mostrarValor"
                >{{ mostrarValor ? 'eye_slash' : 'eye' }}</i>
              </div>
              <div v-if="esCifrada(rform)" class="aviso-cifrado">
                {{ rform && rform.formData && rform.formData.id
                    ? $t('properties.aviso_cifrado_editar')
                    : $t('properties.aviso_cifrado_nueva') }}
              </div>
            </f7-col>

            <f7-col width="100" medium="100" small="100">
              <r-input
                outline
                class="col-100"
                floating-label
                :label="$t('properties.descripcion')"
                :form="rform"
                type="text"
                name="descripcion"
              ></r-input>
            </f7-col>

            <f7-col width="50" medium="50" small="100">
              <r-input
                class="col-100"
                :label="$t('properties.editable')"
                :form="rform"
                type="check"
                name="editable"
              ></r-input>
            </f7-col>

            <f7-col width="50" medium="50" small="100">
              <r-input
                class="col-100"
                :label="$t('properties.activo')"
                :form="rform"
                type="check"
                name="activo"
              ></r-input>
            </f7-col>
          </f7-row>
        </f7-card-content>
      </f7-card>
    </r-form>
  </f7-page>
</template>

<style scoped>
::v-deep .codigo-readonly .readonly .item-input-wrap input {
  background-color: #e9ecef;
}
.valor-wrapper {
  position: relative;
}
/* Mismo patron que el ojo del login. */
.valor-wrapper .input-eye {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 18px;
  color: #6c757d;
  z-index: 10;
}
.aviso-cifrado {
  font-size: 11px;
  color: #6c757d;
  padding: 0 16px 4px 16px;
  line-height: 1.3;
}
</style>

<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import Property from "./../../js/db/Property.js";

export default {
  components: {
    rForm,
    rInput
  },
  data: function() {
    return {
      Property,
      readonly: !this.editable,
      mostrarValor: false,
      defaultData: {
        nombre: "",
        valor: "",
        descripcion: "",
        tipoCifrado: "NONE",
        editable: true,
        activo: true
      }
    };
  },
  props: {
    id: {
      type: String
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    // Al editar una property cifrada se vacia el campo del valor. En modo lectura lo que se ve es
    // la mascara ('********'), y si se dejara ahi el usuario podria escribir detras sin darse
    // cuenta y guardar 'mascara + texto' como valor real. Vacio significa "no cambiar el valor".
    activarEdicion: function() {
      this.limpiarValorCifrado();
      this.mostrarValor = false;
      this.readonly = false;
    },
    // A /properties/edit/:id/ se entra ya en modo edicion, sin pasar por activarEdicion(), asi que
    // hay que vaciar tambien aqui: si no, la mascara recien cargada se queda en un campo editable.
    onLoadItem: function() {
      if (!this.readonly) {
        this.limpiarValorCifrado();
      }
    },
    limpiarValorCifrado: function() {
      var rform = this.$refs.rform;
      if (rform && rform.formData && this.esCifrada(rform)) {
        rform.formData.valor = "";
      }
    },
    esCifrada: function(rform) {
      return !!(
        rform &&
        rform.formData &&
        rform.formData.tipoCifrado &&
        rform.formData.tipoCifrado !== "NONE"
      );
    },
    // Las cifradas se escriben ocultas salvo que se pulse el ojo. Las NONE son configuracion
    // normal y se ven siempre.
    tipoInputValor: function(rform) {
      return this.esCifrada(rform) && !this.mostrarValor ? "password" : "text";
    },
    deleteItem: function() {
      var self = this;
      var app = self.$f7;
      app.dialog.confirm(
        self.$t("rlist.pregunta_eliminar_registro"),
        self.$t("common.eliminar"),
        function() {
          Property.delete({ itemId: self.id })
            .then(function(res) {
              if (res) {
                app.toast
                  .create({
                    icon: '<i class="f7-icons">checkmark</i>',
                    text: self.$t("rlist.registro_eliminado"),
                    position: "center",
                    destroyOnClose: true,
                    closeTimeout: 2000
                  })
                  .open();
                self.$f7router.back();
              }
            })
            .catch(function(error) {
              app.dialog.alert(error.message || error.error || error);
            });
        }
      );
    },
    onSave: function(res) {
      var self = this;
      var app = self.$f7;
      self.$refs.rform.formData = res;
      self.readonly = true;
      app.toast
        .create({
          text: self.$t("common.guardado_ok"),
          position: "center",
          closeTimeout: 2000,
          icon: '<i class="f7-icons">checkmark_alt</i>'
        })
        .open();
    }
  }
};
</script>
